import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { captionLength, platformMeta, PostMediaKind, platformSoftFill } from '../platformMeta';
import { slotLabel } from '../slotLabel';
import { SocialPlatform } from '../types';
import { hashtagParts } from './hashtagParts';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
import { inlineMediaUrl } from '../inlineMediaUrl';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../components/primitives/Panel';
/* eslint-enable css-modules/no-unused-class */

interface ComposerPreviewCardProps {
  platform: SocialPlatform;
  handle: string | null;
  caption: string;
  mediaUrl?: string;
  /** Image or video: an image shows in an img, a video in a video element. */
  mediaKind?: PostMediaKind;
  /** When this post goes out, already worded. */
  whenLabel: string;
  /** ISO 8601. This channel's own peak hour. */
  slot?: string;
}

/**
 * One channel's post as it will read.
 *
 * There is no avatar here, and no follower count: we do not hold the artist's
 * picture or their audience per platform, and a stand-in would make the card a
 * mock-up rather than a preview.
 */
const ComposerPreviewCardComponent: React.FC<ComposerPreviewCardProps> = ({
  platform,
  handle,
  caption,
  mediaUrl,
  mediaKind = 'video',
  whenLabel,
  slot,
}) => {
  const intl = useIntl();
  const meta = platformMeta[platform];
  const parts = useMemo(() => hashtagParts(caption), [caption]);
  // X counts a link as 23 characters.
  const length = captionLength(platform, caption);
  const isOverLimit = length > meta.captionLimit;

  return (
    <Panel className={styles.previewCard} style={{ borderLeftColor: meta.color, borderLeftWidth: 3 }}>
      <PanelHead
        lead={
          <span className={styles.previewMark} style={{ background: platformSoftFill(platform), color: meta.color }}>
            <FontAwesomeIcon icon={meta.icon} />
          </span>
        }
        title={meta.name}
        subtitle={<span className={styles.previewHandle}>{whenLabel}</span>}
        right={
          <span className={clsx(styles.counter, isOverLimit && styles.counterOver)}>
            {`${length}/${meta.captionLimit}`}
          </span>
        }
      />
      <PanelBody stack>
        <div className={styles.previewBody}>
          {parts.map((part, index) => (
            // eslint-disable-next-line react/no-array-index-key -- the parts are positional slices of one string
            <span key={`${part.text}-${index}`} className={part.isHashtag ? styles.hashtag : undefined}>
              {part.text}
            </span>
          ))}
        </div>

        {mediaUrl !== undefined && mediaKind === 'image' ? (
          <img className={styles.previewMedia} src={inlineMediaUrl(mediaUrl)} alt="" />
        ) : null}
        {mediaUrl !== undefined && mediaKind === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- a music clip carries no speech to caption
          <video className={styles.previewMedia} src={inlineMediaUrl(mediaUrl)} muted playsInline />
        ) : null}
      </PanelBody>

      <PanelFoot className={styles.previewFoot}>
        <span>
          {handle ??
            intl.formatMessage({ id: 'app.sml.promote.composer.preview.noHandle', defaultMessage: 'No handle yet' })}
        </span>
        {slot !== undefined ? (
          <span>
            {intl.formatMessage(
              { id: 'app.sml.promote.composer.preview.best', defaultMessage: 'BEST {slot}' },
              { slot: slotLabel(intl, slot) }
            )}
          </span>
        ) : null}
      </PanelFoot>
    </Panel>
  );
};

export const ComposerPreviewCard = TypedMemo(ComposerPreviewCardComponent);
